import { useMemo, useState } from 'react';
import {
  SAMPLE_CSV, CSV_FIELDS, csvToEvents, diffEventSets, describeEventDate, guessMapping, parseCsv, validateEventItem,
  type CsvMapping, type EventItem, type EventSet, type ProjectDoc,
} from '@cal/engine';
import { post, errorMessage } from '../api';
import { Field, Modal, Select, Spinner } from '../ui/controls';
import { toast } from '../ui/toast';
import { useLibrary } from '../library';
import { edit } from './store';
import { useEffect } from 'react';

const newId = () => `e-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;

/** Editable table of events. Used for project snapshots and for library sets. */
export function EventsTable({ events, onChange, yearHint }: { events: EventItem[]; onChange: (e: EventItem[]) => void; yearHint: number }) {
  const upd = (i: number, patch: Partial<EventItem>) => onChange(events.map((e, j) => (j === i ? { ...e, ...patch } : e)));
  return (
    <div className="col">
      <table className="tbl">
        <thead><tr><th>Label</th><th style={{ width: 130 }}>Repeats</th><th>Date(s)</th><th style={{ width: 40 }} /></tr></thead>
        <tbody>
          {events.map((e, i) => {
            const err = validateEventItem(e);
            return (
              <tr key={e.id}>
                <td>
                  <input className={`input ${!e.label.trim() ? 'invalid' : ''}`} value={e.label} aria-label="Event label" onChange={(x) => upd(i, { label: x.target.value })} />
                  {e.observed && <span className="badge info" style={{ marginTop: 4 }}>Observed day{e.originalDate ? ` for ${e.originalDate}` : ''}</span>}
                  {e.declared && <span className="badge accent" style={{ marginTop: 4 }}>Specially declared</span>}
                  {err && <div className="small" style={{ color: 'var(--err)' }}>{err}</div>}
                </td>
                <td>
                  <select className="input" aria-label="Type" value={e.type} onChange={(x) => {
                    const type = x.target.value as EventItem['type'];
                    const base = e.date ?? e.start ?? `${yearHint}-01-01`;
                    if (type === 'date') upd(i, { type, date: base, start: undefined, end: undefined, month: undefined, day: undefined });
                    if (type === 'range') upd(i, { type, start: base, end: e.end ?? base, date: undefined, month: undefined, day: undefined });
                    if (type === 'recurring') upd(i, { type, month: +base.slice(5, 7), day: +base.slice(8, 10), date: undefined, start: undefined, end: undefined });
                  }}>
                    <option value="date">Once (this year)</option>
                    <option value="range">Date range</option>
                    <option value="recurring">Every year</option>
                  </select>
                </td>
                <td>
                  {e.type === 'date' && <input className="input" type="date" aria-label="Date" value={e.date ?? ''} onChange={(x) => upd(i, { date: x.target.value })} />}
                  {e.type === 'range' && <div className="row"><input className="input" type="date" aria-label="Start date" value={e.start ?? ''} onChange={(x) => upd(i, { start: x.target.value })} /><span>→</span><input className="input" type="date" aria-label="End date" value={e.end ?? ''} onChange={(x) => upd(i, { end: x.target.value })} /></div>}
                  {e.type === 'recurring' && (
                    <div className="row">
                      <input className="input" type="number" min={1} max={31} aria-label="Day" style={{ width: 70 }} value={e.day ?? 1} onChange={(x) => upd(i, { day: +x.target.value })} />
                      <select className="input" aria-label="Month" value={e.month ?? 1} onChange={(x) => upd(i, { month: +x.target.value })}>
                        {['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'].map((m, k) => <option key={m} value={k + 1}>{m}</option>)}
                      </select>
                    </div>
                  )}
                </td>
                <td><button className="btn sm ghost danger" aria-label={`Delete ${e.label}`} onClick={() => onChange(events.filter((_, j) => j !== i))}>✕</button></td>
              </tr>
            );
          })}
        </tbody>
      </table>
      {!events.length && <p className="small faint">No entries yet.</p>}
      <div className="row">
        <button className="btn sm" onClick={() => onChange([...events, { id: newId(), label: '', type: 'recurring', month: 1, day: 1, origin: 'manual' }])}>+ Every-year date</button>
        <button className="btn sm" onClick={() => onChange([...events, { id: newId(), label: '', type: 'date', date: `${yearHint}-01-01`, origin: 'manual' }])}>+ Single date</button>
        <button className="btn sm" onClick={() => onChange([...events, { id: newId(), label: '', type: 'range', start: `${yearHint}-01-01`, end: `${yearHint}-01-07`, origin: 'manual' }])}>+ Date range</button>
      </div>
    </div>
  );
}

export function EventSetEditor({ doc, setId, onClose }: { doc: ProjectDoc; setId: string; onClose: () => void }) {
  const set = doc.eventSets.find((s) => s.id === setId)!;
  const [events, setEvents] = useState<EventItem[]>(set.events.length ? set.events : [{ id: newId(), label: '', type: 'recurring', month: 1, day: 1, origin: 'manual' }]);
  const [name, setName] = useState(set.name);
  const invalid = events.some((e) => validateEventItem(e));
  return (
    <Modal wide title={`Edit “${set.name}”`} onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={invalid || !name.trim()} onClick={() => {
        const changed = JSON.stringify(events) !== JSON.stringify(set.events);
        edit((d) => ({ ...d, eventSets: d.eventSets.map((s) => (s.id === setId ? { ...s, name, events, verification: changed && s.verification === 'verified' ? 'manual' : s.verification, verificationNote: changed && s.verification === 'verified' ? 'Edited in this project after verification.' : s.verificationNote } : s)) }), 'events');
        onClose();
      }}>Apply</button></>}>
      <Field label="Set name"><input className="input" value={name} onChange={(e) => setName(e.target.value)} /></Field>
      {set.verification === 'verified' && <div className="notice warn small">This is verified official data. Editing it here marks this project's copy as manually changed.</div>}
      <EventsTable events={events} onChange={setEvents} yearHint={doc.year} />
    </Modal>
  );
}

/** CSV import with sample, column mapping, validation, duplicate handling and a review step. */
export function CsvImport({ doc, setId, onClose }: { doc: ProjectDoc; setId: string; onClose: () => void }) {
  const set = doc.eventSets.find((s) => s.id === setId)!;
  const [rows, setRows] = useState<string[][] | null>(null);
  const [mapping, setMapping] = useState<CsvMapping>({});
  const [hasHeader, setHasHeader] = useState(true);
  const [skipDupes, setSkipDupes] = useState(true);
  const results = useMemo(() => (rows ? csvToEvents(rows, mapping, set.events, newId, hasHeader) : []), [rows, mapping, set.events, hasHeader]);
  const toAdd = results.filter((r) => r.status === 'new' || (!skipDupes && r.status === 'duplicate'));
  const header = rows?.[0] ?? [];
  const sampleHref = `data:text/csv;charset=utf-8,${encodeURIComponent(SAMPLE_CSV)}`;
  return (
    <Modal wide title={`Import CSV into “${set.name}”`} onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Cancel</button>
      <button className="btn primary" disabled={!toAdd.length} onClick={() => {
        edit((d) => ({ ...d, eventSets: d.eventSets.map((s) => (s.id === setId ? { ...s, events: [...s.events, ...toAdd.map((r) => r.item!)] } : s)) }), 'csv');
        toast(`Imported ${toAdd.length} event(s)`, 'ok');
        onClose();
      }}>Import {toAdd.length} event{toAdd.length === 1 ? '' : 's'}</button></>}>
      <p className="small muted">Columns: label, date (YYYY-MM-DD or DD/MM/YYYY), optional end date for ranges, recurring (yes/no) and note. <a href={sampleHref} download="calendar-events-sample.csv">Download a sample CSV</a>.</p>
      <input type="file" accept=".csv,text/csv" aria-label="CSV file" onChange={async (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        if (f.size > 2e6) return toast('CSV is larger than 2 MB.', 'err');
        const parsed = parseCsv(await f.text());
        if (!parsed.length) return toast('The file has no rows.', 'err');
        setRows(parsed);
        setMapping(guessMapping(parsed[0]));
      }} />
      {rows && (
        <>
          <label className="check"><input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />First row is a header</label>
          <div className="grid4" style={{ gridTemplateColumns: 'repeat(5, 1fr)' }}>
            {CSV_FIELDS.map((f) => (
              <Select key={f} label={f === 'end' ? 'End date' : f[0].toUpperCase() + f.slice(1)} value={mapping[f] ?? -1}
                onChange={(v) => setMapping({ ...mapping, [f]: v === -1 ? undefined : v })}
                options={[{ value: -1, label: '— none —' }, ...header.map((h, i) => ({ value: i, label: hasHeader ? h || `Column ${i + 1}` : `Column ${i + 1}` }))]} />
            ))}
          </div>
          <label className="check"><input type="checkbox" checked={skipDupes} onChange={(e) => setSkipDupes(e.target.checked)} />Skip duplicates (same label and date already in the set or the file)</label>
          <div className="row small">
            <span className="badge ok">{results.filter((r) => r.status === 'new').length} new</span>
            <span className="badge warn">{results.filter((r) => r.status === 'duplicate').length} duplicates</span>
            <span className="badge err">{results.filter((r) => r.status === 'error').length} errors (not imported)</span>
          </div>
          <table className="tbl">
            <thead><tr><th>Row</th><th>Status</th><th>Event</th><th>Date</th></tr></thead>
            <tbody>
              {results.map((r) => (
                <tr key={r.row}>
                  <td>{r.row}</td>
                  <td><span className={`badge ${r.status === 'new' ? 'ok' : r.status === 'duplicate' ? 'warn' : 'err'}`}>{r.status}</span></td>
                  <td>{r.item?.label ?? <span style={{ color: 'var(--err)' }}>{r.message}</span>}</td>
                  <td className="small">{r.item ? describeEventDate(r.item) : ''}{r.status === 'duplicate' && <div className="faint">{r.message}</div>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </Modal>
  );
}

export function LibraryPicker({ doc, onClose }: { doc: ProjectDoc; onClose: () => void }) {
  const sets = useLibrary((s) => s.eventSets);
  const load = useLibrary((s) => s.loadEventSets);
  const [loading, setLoading] = useState(true);
  useEffect(() => { load().finally(() => setLoading(false)); }, [load]);
  const inProject = new Set(doc.eventSets.map((s) => s.libraryId));
  const add = async (id: string) => {
    try {
      const { eventSets } = await post<{ eventSets: EventSet[] }>(`/api/event-sets/snapshot`, { ids: [id] });
      edit((d) => ({ ...d, eventSets: [...d.eventSets, ...eventSets] }), 'add-set');
      toast(`Added “${eventSets[0].name}”`, 'ok');
    } catch (e) { toast(errorMessage(e), 'err'); }
  };
  return (
    <Modal wide title="Add holidays or events from the library" onClose={onClose} footer={<button className="btn" onClick={onClose}>Done</button>}>
      {loading ? <Spinner label="Loading library…" /> : (
        <table className="tbl">
          <thead><tr><th>Set</th><th>Year</th><th>Status</th><th /></tr></thead>
          <tbody>
            {sets.map((s) => (
              <tr key={s.id}>
                <td><strong>{s.name}</strong><div className="small faint">{s.events.length} entries · {s.source.slice(0, 120)}{s.source.length > 120 ? '…' : ''}</div></td>
                <td>{s.year ?? 'Every year'}{s.year && s.year !== doc.year && <div className="small" style={{ color: 'var(--err)' }}>Not {doc.year}</div>}</td>
                <td><span className={`badge ${s.verification === 'verified' ? 'ok' : s.verification === 'manual' ? 'info' : 'warn'}`}>{s.verification}</span></td>
                <td>{inProject.has(s.id) ? <span className="badge">Added</span> : <button className="btn sm" disabled={!!s.year && s.year !== doc.year} onClick={() => add(s.id)}>Add</button>}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && !sets.some((s) => s.year === doc.year && s.kind === 'public') && (
        <div className="notice warn small">No public holiday set for {doc.year} in the library. Open “Holidays & events” from the main menu to generate a calculated set (marked unverified) or enter one.</div>
      )}
    </Modal>
  );
}

export function RefreshFromLibrary({ doc, setId, onClose }: { doc: ProjectDoc; setId: string; onClose: () => void }) {
  const snap = doc.eventSets.find((s) => s.id === setId)!;
  const [fresh, setFresh] = useState<EventSet | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    post<{ eventSets: EventSet[] }>(`/api/event-sets/snapshot`, { ids: [snap.libraryId!] }).then((r) => setFresh(r.eventSets[0])).catch((e) => setErr(errorMessage(e)));
  }, [snap.libraryId]);
  const diff = fresh ? diffEventSets(snap, fresh) : null;
  const none = diff && !diff.added.length && !diff.removed.length && !diff.changed.length && !diff.metadataChanged.length;
  return (
    <Modal title={`Updates for “${snap.name}”`} onClose={onClose} footer={<>
      <button className="btn" onClick={onClose}>Close</button>
      {diff && !none && <button className="btn primary" onClick={() => {
        edit((d) => ({ ...d, eventSets: d.eventSets.map((s) => (s.id === setId ? { ...fresh!, id: s.id, display: s.display } : s)) }), 'refresh');
        toast('Event data refreshed. Review affected months.', 'ok');
        onClose();
      }}>Apply these changes</button>}</>}>
      {err && <div className="notice err">{err}</div>}
      {!diff && !err && <Spinner label="Comparing with the library…" />}
      {none && <div className="notice ok">This project already uses the latest library version ({snap.libraryVersion}).</div>}
      {diff && !none && (
        <div className="col small">
          <p>Library version {fresh!.libraryVersion} vs this project's copy (version {snap.libraryVersion}, taken {snap.snapshotAt?.slice(0, 10)}):</p>
          {diff.added.map((e) => <div key={e.id} className="notice ok">Added: {e.label} — {describeEventDate(e)}</div>)}
          {diff.removed.map((e) => <div key={e.id} className="notice err">Removed: {e.label} — {describeEventDate(e)}</div>)}
          {diff.changed.map((c) => <div key={c.after.id} className="notice warn">Changed: {c.before.label} ({describeEventDate(c.before)}) → {c.after.label} ({describeEventDate(c.after)})</div>)}
          {diff.metadataChanged.length > 0 && <div className="notice info">Details changed: {diff.metadataChanged.join(', ')} (now {fresh!.verification})</div>}
          <p className="muted">Your display settings (colours, labels, shading) are kept. If the project was approved, applying changes requires a new approval.</p>
        </div>
      )}
    </Modal>
  );
}
